import Select, { type SelectItemType } from '@orn/shared-web/components/Select/Select';
import SelectItem from '@orn/shared-web/components/Select/SelectItem';
import * as React from 'react';

import { clampEndToStart, endHourOptions, startHourOptions } from './outreachWindow';

export interface IOutreachHoursFieldsProps {
  /** Local wall-clock `HH:MM` the window opens. */
  start: string;
  /** Local wall-clock `HH:MM` the window closes, exclusive; `23:59` is the end of the day. */
  end: string;
  /** Both edges are handed back together — a half-window write would drop the other edge. */
  onChange: (hours: { start: string; end: string }) => void;
  /** The IANA zone the window is read in, named in the help text so the hours are unambiguous. */
  timezone: string;
}

/**
 * The outreach window's two hour dropdowns. Fully controlled; every change
 * emits a complete `{ start, end }` pair, and moving the start past the end
 * carries the end along rather than leaving an invalid window on screen.
 */
const OutreachHoursFields = ({ start, end, onChange, timezone }: IOutreachHoursFieldsProps) => {
  // Both lists carry the stored edge when it misses the hour grid — the
  // assistant can write any `HH:MM`, and a `selectedKey` the list omits renders
  // as the placeholder, hiding the member's window from them.
  const startOptions = React.useMemo(() => startHourOptions(start), [start]);
  const endOptions = React.useMemo(() => endHourOptions(start, end), [start, end]);

  return (
    <div className="flex max-w-sm flex-col gap-2">
      <div className="flex items-end gap-3">
        <Select
          label="From"
          placeholder="Select"
          selectedKey={start}
          onSelectionChange={(key) => {
            const nextStart = key as string;
            onChange({ start: nextStart, end: clampEndToStart(nextStart, end) });
          }}
          items={startOptions}
          size="md"
        >
          {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
        </Select>
        <Select
          label="Until"
          placeholder="Select"
          selectedKey={end}
          onSelectionChange={(key) => {
            onChange({ start, end: key as string });
          }}
          items={endOptions}
          size="md"
        >
          {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
        </Select>
      </div>
      <span className="text-tertiary text-sm">Times are in your local timezone ({timezone}).</span>
    </div>
  );
};

OutreachHoursFields.displayName = 'OutreachHoursFields';

export default OutreachHoursFields;
